/**
 * GitChain MCP tools — consolidated chain lifecycle operations.
 *
 * 4 tools (down from 14) covering CRUD, versioning, branching, and review.
 * Backend calls are unchanged — same Convex `gitchain.*` namespace.
 *
 * Chain: GT-vaw0re (Git Chain), FEAT-CW-001 (ChainWork Engine)
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, getWorkspaceContext, runWithToolContext } from "../client.js";
import { trackWriteTool } from "../tool-surface.js";
import { thinWrapper, success, failure, validationResult, successResult, unknownAction, parseOrFail, type ToolResult } from "../envelope.js";
import { toVersionDisplay } from "../lib/versionDisplay.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";

export const chainSchema = z.object({
  action: z.enum(["create", "get", "list", "edit"])
    .describe("Action: create a process, get process details, list all processes, or edit a process link"),
  chainEntryId: z.string().max(200).optional()
    .describe("Chain entry ID (required for get/edit)"),
  title: z.string().max(500).optional()
    .describe("Process title (required for create)"),
  chainTypeId: z.string().max(200).optional().default("strategy-coherence")
    .describe("Process template slug for create: 'strategy-coherence', 'idm-proposal', or any custom template slug"),
  description: z.string().max(20000).optional()
    .describe("Description (for create)"),
  linkId: z.string().max(200).optional()
    .describe("Link to edit (for edit action): problem, insight, choice, action, outcome"),
  content: z.string().max(50000).optional()
    .describe("New content for the link (for edit action)"),
  status: z.string().max(200).optional()
    .describe("Filter by status for list: 'draft' or 'active'"),
  author: z.string().max(200).optional()
    .describe("Who is performing the action. Defaults to 'mcp'."),
});

export const chainVersionSchema = z.object({
  action: z.enum(["commit", "list", "diff", "revert", "history"])
    .describe("Action: commit a snapshot, list commits, diff two versions, revert to a version, or view history"),
  chainEntryId: z.string().max(200).describe("The chain's entry ID"),
  commitMessage: z.string().max(2000).optional()
    .describe("Commit message (required for commit). Convention: type(link): description"),
  versionA: z.number().optional().describe("Earlier version for diff"),
  versionB: z.number().optional().describe("Later version for diff"),
  toVersion: z.number().optional().describe("Version number to revert to"),
  author: z.string().max(200).optional().describe("Who is performing the action. Defaults to 'mcp'."),
});

export const chainBranchSchema = z.object({
  action: z.enum(["create", "list", "merge", "conflicts"])
    .describe("Action: create a branch, list branches, merge a branch, or check for conflicts"),
  chainEntryId: z.string().max(200).describe("The chain's entry ID"),
  branchName: z.string().max(200).optional()
    .describe("Branch name (required for merge/conflicts, optional for create)"),
  strategy: z.enum(["merge_commit", "squash"]).optional()
    .describe("Merge strategy: 'merge_commit' (default) or 'squash'"),
  author: z.string().max(200).optional().describe("Who is performing the action. Defaults to 'mcp'."),
});

export const chainReviewSchema = z.object({
  action: z.enum(["gate", "comment", "resolve-comment", "list-comments"])
    .describe("Action: run coherence gate, add a comment, resolve a comment, or list comments"),
  // Finding #12: optional at the base (mirrors chainSchema's chainEntryId pattern at
  // line ~690) — resolve-comment resolves purely by commentId (handleChainReview never
  // reads chainEntryId in that branch) and the compound-tool's advertised schema
  // (chainReviewCompoundSchema below) already documents it as optional for that action.
  // Per-action variants that DO need it (gate/comment/list-comments) re-require it below,
  // same pattern as chainGetVariant/chainEditVariant re-requiring over chainSchema's base.
  chainEntryId: z.string().max(200).optional().describe("The chain's entry ID. Required for every action except 'resolve-comment'."),
  commitMessage: z.string().max(2000).optional()
    .describe("Commit message to lint (for gate action)"),
  versionNumber: z.number().optional()
    .describe("Version to comment on or list comments for"),
  linkId: z.string().max(200).optional()
    .describe("Link this comment targets (optional for comment)"),
  body: z.string().max(20000).optional()
    .describe("Comment text (required for comment action)"),
  commentId: z.string().max(200).optional()
    .describe("Comment ID (required for resolve-comment)"),
  author: z.string().max(200).optional().describe("Who is performing the action. Defaults to 'mcp'."),
});

function linkSummary(links: Record<string, string>): string {
  return Object.entries(links)
    .map(([id, content]) => {
      const filled = typeof content === "string" && content.length > 0;
      const preview = filled
        ? content.substring(0, 80) + (content.length > 80 ? "..." : "")
        : "(empty)";
      return `  - **${id}**: ${preview}`;
    })
    .join("\n");
}

/**
 * WP-484 S2 (§3): extracted CRUD handler for the legacy `chain` tool —
 * reused by the legacy standalone registration AND the new module-gated
 * `chain` compound tool's unnamespaced create/get/list/edit actions
 * (absorbs chain-version/chain-branch/chain-review as namespaced actions,
 * §3 module-gated table). One business-logic path.
 */
export async function handleChainCrud({ action, chainEntryId, title, chainTypeId, description, linkId, content, status, author }: z.infer<typeof chainSchema>) {
      if (action === "create") {
        if (!title) {
          return validationResult("A `title` is required to create a process.");
        }

        const result = await kernelMutation<{ _id: string; entryId: string }>(
          "gitchain.createChain",
          { title, chainTypeId, description, author }
        );

        return {
          content: [{
            type: "text" as const,
            text:
              `# Process Created\n\n` +
              `- **Entry ID:** \`${result.entryId}\`\n` +
              `- **Title:** ${title}\n` +
              `- **Type:** ${chainTypeId}\n` +
              `- **Status:** draft\n\n` +
              `Use \`chain action=edit chainEntryId="${result.entryId}" linkId="problem" content="..."\` to start filling in links.`,
          }],
          structuredContent: success(
            `Created process '${title}' (${result.entryId}), type: ${chainTypeId}.`,
            { entryId: result.entryId, title, chainTypeId, status: "draft" },
            [{ tool: "chain", description: "Edit a link", parameters: { action: "edit", chainEntryId: result.entryId, linkId: "problem" } }],
          ),
        };
      }

      if (action === "get") {
        if (!chainEntryId) {
          return validationResult("A `chainEntryId` is required.");
        }

        const chain = await kernelQuery<any>("gitchain.getChain", { chainEntryId });
        if (!chain) {
          return {
            content: [{ type: "text" as const, text: `Process "${chainEntryId}" not found.` }],
            structuredContent: failure(
              "NOT_FOUND",
              `Process "${chainEntryId}" not found.`,
              "Use chain action=list to find available processes.",
              [{ tool: "chain", description: "List processes", parameters: { action: "list" } }],
            ),
          };
        }

        const scoreSection = chain.scores
          ? `\n## Coherence: ${chain.coherenceScore}%\n\n` +
            chain.scores.sections
              .map((s: any) => `- **${s.key}**: ${s.power}/100 (${"★".repeat(Math.min(s.stars ?? 0, 5))}${"☆".repeat(Math.max(0, 5 - (s.stars ?? 0)))})`)
              .join("\n")
          : "";

        return {
          content: [{
            type: "text" as const,
            text:
              `# ${chain.name}\n\n` +
              `- **Entry ID:** \`${chain.entryId}\`\n` +
              `- **Type:** ${chain.chainTypeName}\n` +
              `- **Status:** ${chain.status}\n` +
              `- **Links filled:** ${chain.filledCount}/${chain.totalCount}\n` +
              `- **Version:** ${toVersionDisplay(chain.currentVersion)}\n` +
              `- **Created by:** ${chain.createdBy ?? "unknown"}\n` +
              `- **History events:** ${chain.historyCount}\n` +
              scoreSection +
              `\n\n## Links\n\n` +
              linkSummary(chain.links),
          }],
          structuredContent: success(
            `${chain.name}: ${chain.filledCount}/${chain.totalCount} links, coherence ${chain.coherenceScore}%, ${toVersionDisplay(chain.currentVersion)}.`,
            { entryId: chain.entryId, name: chain.name, status: chain.status, filledCount: chain.filledCount, totalCount: chain.totalCount, coherenceScore: chain.coherenceScore, version: chain.currentVersion ?? null, versionDisplay: toVersionDisplay(chain.currentVersion) },
          ),
        };
      }

      if (action === "list") {
        const chains = await kernelQuery<any[]>("gitchain.listChains", { chainTypeId, status });

        if (chains.length === 0) {
          return {
            content: [{ type: "text" as const, text: "No processes found. Use `chain action=create` to create one." }],
            structuredContent: success(
              "No processes found.",
              { chains: [], total: 0 },
              [{ tool: "chain", description: "Create a process", parameters: { action: "create" } }],
            ),
          };
        }

        const formatted = chains
          .map((c) =>
            `- **\`${c.entryId}\`** ${c.name} — ${c.chainTypeId} · ` +
            `${c.filledCount}/${c.totalCount} links · ` +
            `coherence: ${c.coherenceScore}% · status: ${c.status}`
          )
          .join("\n");

        return {
          content: [{ type: "text" as const, text: `# Chains (${chains.length})\n\n${formatted}` }],
          structuredContent: success(
            `Found ${chains.length} process(es).`,
            { total: chains.length },
          ),
        };
      }

      if (action === "edit") {
        if (!chainEntryId) {
          return validationResult("A `chainEntryId` is required.");
        }
        if (!linkId) {
          return validationResult("A `linkId` is required (e.g. problem, insight, choice, action, outcome).");
        }
        if (!content) {
          return validationResult("The `content` for the link is required.");
        }

        const result = await kernelMutation<{
          _id: string; entryId: string; linkId: string; status: string;
        }>("gitchain.editLink", { chainEntryId, linkId, content, author });

        return {
          content: [{
            type: "text" as const,
            text:
              `# Link Updated\n\n` +
              `- **Chain:** \`${result.entryId}\`\n` +
              `- **Link:** ${result.linkId}\n` +
              `- **Chain status:** ${result.status}\n` +
              `- **Content length:** ${content.length} chars\n\n` +
              `Use \`chain action=get\` to see the full chain with updated scores.`,
          }],
          structuredContent: success(
            `Updated link "${result.linkId}" on ${result.entryId}. Status: ${result.status}.`,
            { entryId: result.entryId, linkId: result.linkId, status: result.status, contentLength: content.length },
            [{ tool: "chain", description: "View full chain", parameters: { action: "get", chainEntryId } }],
          ),
        };
      }

      return unknownAction(action, ["create", "get", "list", "edit"]);
}

/**
 * WP-484 S2 (§3): extracted version handler — reused by the legacy
 * `chain-version` tool AND the new `chain` compound tool's `version.*`
 * namespaced actions. One business-logic path.
 */
export async function handleChainVersion({ action, chainEntryId, commitMessage, versionA, versionB, toVersion, author }: z.infer<typeof chainVersionSchema>) {
      if (action === "commit") {
        if (!commitMessage) {
          return validationResult("A `commitMessage` is required.");
        }

        const result = await kernelMutation<{
          entryId: string; version: number; coherenceScore: number;
          linksModified: string[]; commitLintWarning: string | null;
        }>("gitchain.commitChain", { chainEntryId, commitMessage, author });

        const warning = result.commitLintWarning
          ? `\n\n> **Lint warning:** ${result.commitLintWarning}`
          : "";

        return {
          content: [{
            type: "text" as const,
            text:
              `# Committed v${result.version}\n\n` +
              `- **Chain:** \`${result.entryId}\`\n` +
              `- **Version:** ${result.version}\n` +
              `- **Message:** ${commitMessage}\n` +
              `- **Coherence:** ${result.coherenceScore}%\n` +
              `- **Links modified:** ${result.linksModified.length > 0 ? result.linksModified.join(", ") : "none"}\n` +
              warning,
          }],
          structuredContent: success(
            `Committed v${result.version} on ${result.entryId}. Coherence: ${result.coherenceScore}%.`,
            { entryId: result.entryId, version: result.version, versionDisplay: toVersionDisplay(result.version), coherenceScore: result.coherenceScore, linksModified: result.linksModified, lintWarning: result.commitLintWarning },
          ),
        };
      }

      if (action === "list") {
        const commits = await kernelQuery<any[]>("gitchain.listCommits", { chainEntryId });

        if (commits.length === 0) {
          return {
            content: [{ type: "text" as const, text: `No commits found for chain "${chainEntryId}". Use \`chain action=version.commit\` to create the first snapshot.` }],
            structuredContent: success(
              `No commits for ${chainEntryId}.`,
              { chainEntryId, commits: [], total: 0 },
              [{ tool: "chain", description: "Commit chain", parameters: { action: "version.commit", chainEntryId } }],
            ),
          };
        }

        const formatted = commits
          .map((c: any) => {
            const date = new Date(c.createdAt).toISOString().replace("T", " ").substring(0, 19);
            const msg = c.commitMessage ?? c.changeNote ?? "(no message)";
            const links = c.linksModified?.length > 0 ? ` [${c.linksModified.join(", ")}]` : "";
            return `- **v${c.version}** ${date} by ${c.author} — ${msg}${links} (${c.versionStatus})`;
          })
          .join("\n");

        return {
          content: [{ type: "text" as const, text: `# Commits for ${chainEntryId} (${commits.length})\n\n${formatted}` }],
          structuredContent: success(
            `${commits.length} commit(s) for ${chainEntryId}.`,
            { chainEntryId, total: commits.length },
          ),
        };
      }

      if (action === "diff") {
        if (versionA == null || versionB == null) {
          return validationResult("Both `versionA` and `versionB` are required for diff.");
        }

        const diff = await kernelMutation<any>("gitchain.diffVersions", { chainEntryId, versionA, versionB });

        let text =
          `# Diff: v${versionA} → v${versionB}\n\n` +
          `- **Chain:** \`${diff.chainEntryId}\`\n` +
          `- **Coherence:** ${diff.coherenceBefore}% → ${diff.coherenceAfter}% (${diff.coherenceDelta >= 0 ? "+" : ""}${diff.coherenceDelta})\n` +
          `- **Links changed:** ${diff.linksChanged.length > 0 ? diff.linksChanged.join(", ") : "none"}\n`;

        for (const ld of diff.linkDiffs) {
          if (ld.status === "unchanged") continue;
          text += `\n## ${ld.linkId} (${ld.status})\n\n`;
          const wordDiff = diff.wordDiffs[ld.linkId];
          if (wordDiff?.length > 0) {
            for (const w of wordDiff) {
              if (w.type === "delete") text += `~~${w.value.substring(0, 200)}~~`;
              else if (w.type === "insert") text += `**${w.value.substring(0, 200)}**`;
              else text += w.value.substring(0, 200);
            }
            text += "\n";
          }
        }

        return {
          content: [{ type: "text" as const, text }],
          structuredContent: success(
            `Diff v${versionA} → v${versionB}: coherence ${diff.coherenceBefore}% → ${diff.coherenceAfter}%, ${diff.linksChanged.length} link(s) changed.`,
            { chainEntryId: diff.chainEntryId, versionA, versionB, coherenceBefore: diff.coherenceBefore, coherenceAfter: diff.coherenceAfter, coherenceDelta: diff.coherenceDelta, linksChanged: diff.linksChanged },
          ),
        };
      }

      if (action === "revert") {
        if (toVersion == null) {
          return validationResult("A `toVersion` is required for revert.");
        }

        const result = await kernelMutation<{
          entryId: string; revertedTo: number; newVersion: number; linksModified: string[];
        }>("gitchain.revertChain", { chainEntryId, toVersion, author });

        return {
          content: [{
            type: "text" as const,
            text:
              `# Reverted\n\n` +
              `- **Chain:** \`${result.entryId}\`\n` +
              `- **Reverted to:** v${result.revertedTo}\n` +
              `- **New version:** v${result.newVersion}\n` +
              `- **Links affected:** ${result.linksModified.length > 0 ? result.linksModified.join(", ") : "none"}\n\n` +
              `History is preserved — this created a new version, not a destructive reset.`,
          }],
          structuredContent: success(
            `Reverted ${result.entryId} to v${result.revertedTo}. New version: v${result.newVersion}.`,
            { entryId: result.entryId, revertedTo: result.revertedTo, revertedToDisplay: toVersionDisplay(result.revertedTo), newVersion: result.newVersion, newVersionDisplay: toVersionDisplay(result.newVersion), linksModified: result.linksModified },
          ),
        };
      }

      if (action === "history") {
        const history = await kernelQuery<any[]>("gitchain.getHistory", { chainEntryId });

        if (history.length === 0) {
          return {
            content: [{ type: "text" as const, text: `No history found for chain "${chainEntryId}".` }],
            structuredContent: success(
              `No history for ${chainEntryId}.`,
              { chainEntryId, events: [], total: 0 },
            ),
          };
        }

        const formatted = history
          .sort((a: any, b: any) => b.timestamp - a.timestamp)
          .map((h: any) => {
            const date = new Date(h.timestamp).toISOString().replace("T", " ").substring(0, 19);
            return `- **${date}** [${h.event}] by ${h.changedBy ?? "unknown"} — ${h.note ?? ""}`;
          })
          .join("\n");

        return {
          content: [{ type: "text" as const, text: `# History for ${chainEntryId} (${history.length} events)\n\n${formatted}` }],
          structuredContent: success(
            `${history.length} history event(s) for ${chainEntryId}.`,
            { chainEntryId, total: history.length },
          ),
        };
      }

      return unknownAction(action, ["commit", "list", "diff", "revert", "history"]);
}

/**
 * WP-484 S2 (§3): extracted branch handler — reused by the legacy
 * `chain-branch` tool AND the new `chain` compound tool's `branch.*`
 * namespaced actions. One business-logic path.
 */
export async function handleChainBranch({ action, chainEntryId, branchName, strategy, author }: z.infer<typeof chainBranchSchema>) {
      if (action === "create") {
        const result = await kernelMutation<{
          branchId: string; name: string; baseVersion: number;
        }>("gitchain.createBranch", { chainEntryId, name: branchName, author });

        return {
          content: [{
            type: "text" as const,
            text:
              `# Branch Created\n\n` +
              `- **Name:** ${result.name}\n` +
              `- **Based on:** v${result.baseVersion}\n` +
              `- **Chain:** \`${chainEntryId}\`\n\n` +
              `Edit links and commit on this branch, then use \`chain-review action=branch.merge\` to land changes.`,
          }],
          structuredContent: success(
            `Created branch "${result.name}" based on v${result.baseVersion}.`,
            { branchId: result.branchId, name: result.name, baseVersion: result.baseVersion, chainEntryId },
            [{ tool: "chain-review", description: "Merge branch", parameters: { action: "branch.merge", chainEntryId, branchName: result.name } }],
          ),
        };
      }

      if (action === "list") {
        const branches = await kernelMutation<any[]>("gitchain.listBranches", { chainEntryId });

        if (branches.length === 0) {
          return {
            content: [{ type: "text" as const, text: `No branches found for chain "${chainEntryId}".` }],
            structuredContent: success(
              `No branches for ${chainEntryId}.`,
              { chainEntryId, branches: [], total: 0 },
              [{ tool: "chain-review", description: "Create a branch", parameters: { action: "branch.create", chainEntryId } }],
            ),
          };
        }

        const formatted = branches
          .map((b: any) => `- **${b.name}** (${b.status}) — based on v${b.baseVersion}, by ${b.createdBy}`)
          .join("\n");

        return {
          content: [{ type: "text" as const, text: `# Branches for ${chainEntryId} (${branches.length})\n\n${formatted}` }],
          structuredContent: success(
            `${branches.length} branch(es) for ${chainEntryId}.`,
            { chainEntryId, total: branches.length },
          ),
        };
      }

      if (action === "merge") {
        if (!branchName) {
          return validationResult("A `branchName` is required for merge.");
        }

        const result = await kernelMutation<{
          entryId: string; branchName: string; version: number; strategy: string;
        }>("gitchain.mergeBranch", { chainEntryId, branchName, strategy, author });

        return {
          content: [{
            type: "text" as const,
            text:
              `# Branch Merged\n\n` +
              `- **Chain:** \`${result.entryId}\`\n` +
              `- **Branch:** ${result.branchName} (now closed)\n` +
              `- **Version:** v${result.version}\n` +
              `- **Strategy:** ${result.strategy}\n\n` +
              `Main is now at v${result.version}. The branch has been closed.`,
          }],
          structuredContent: success(
            `Merged branch "${result.branchName}" into ${result.entryId} at v${result.version}.`,
            { entryId: result.entryId, branchName: result.branchName, version: result.version, versionDisplay: toVersionDisplay(result.version), strategy: result.strategy },
          ),
        };
      }

      if (action === "conflicts") {
        if (!branchName) {
          return validationResult("A `branchName` is required for conflict check.");
        }

        const result = await kernelMutation<any>("gitchain.checkConflicts", { chainEntryId, branchName });

        if (!result.hasConflicts) {
          return {
            content: [{
              type: "text" as const,
              text: `# No Conflicts\n\nBranch "${branchName}" on \`${chainEntryId}\` has no conflicts. Safe to merge.`,
            }],
            structuredContent: success(
              `No conflicts on branch "${branchName}". Safe to merge.`,
              { chainEntryId, branchName, hasConflicts: false },
              [{ tool: "chain-review", description: "Merge branch", parameters: { action: "branch.merge", chainEntryId, branchName } }],
            ),
          };
        }

        const conflictLines = result.conflicts
          .map((c: any) =>
            `- **${c.linkId}** — modified by: ${c.branches.map((b: any) => `${b.branchName} (${b.author})`).join(", ")}`
          )
          .join("\n");

        return {
          content: [{
            type: "text" as const,
            text:
              `# Conflicts Detected\n\n` +
              `Branch "${branchName}" conflicts with other branches on these links:\n\n` +
              conflictLines +
              `\n\nResolve conflicts before merging.`,
          }],
          structuredContent: failure(
            "CONFLICT",
            `Branch "${branchName}" has ${result.conflicts.length} conflict(s).`,
            "Resolve conflicts on the conflicting links before merging.",
          ),
        };
      }

      return unknownAction(action, ["create", "list", "merge", "conflicts"]);
}

/**
 * WP-484 S2 (§3): extracted review handler — reused by the legacy
 * `chain-review` tool AND the new `chain` compound tool's `review.*`
 * namespaced actions. One business-logic path.
 */
export async function handleChainReview({ action, chainEntryId, commitMessage, versionNumber, linkId, body, commentId, author }: z.infer<typeof chainReviewSchema>) {
      if (action === "gate") {
        const gate = await kernelQuery<any>("gitchain.runGate", { chainEntryId, commitMessage });

        const checkLines = gate.checks
          .map((c: any) => `- ${c.pass ? "PASS" : "FAIL"} **${c.name}**: ${c.detail}`)
          .join("\n");

        const icon = gate.pass ? "PASS" : "BLOCKED";

        return {
          content: [{
            type: "text" as const,
            text:
              `# Gate: ${icon}\n\n` +
              `- **Score:** ${gate.score}%\n` +
              `- **Threshold:** ${gate.threshold}%\n\n` +
              `## Checks\n\n${checkLines}`,
          }],
          structuredContent: success(
            `Gate ${icon}: ${gate.score}% (threshold ${gate.threshold}%).`,
            { pass: gate.pass, score: gate.score, threshold: gate.threshold, checks: gate.checks },
          ),
        };
      }

      if (action === "comment") {
        if (!versionNumber) {
          return validationResult("A `versionNumber` is required.");
        }
        if (!body) {
          return validationResult("A `body` is required.");
        }

        const result = await kernelMutation<any>("gitchain.addComment", {
          chainEntryId, versionNumber, linkId, body, author,
        });

        return {
          content: [{
            type: "text" as const,
            text:
              `# Comment Added\n\n` +
              `- **Chain:** \`${result.chainEntryId}\`\n` +
              `- **Version:** v${result.versionNumber}\n` +
              (result.linkId ? `- **Link:** ${result.linkId}\n` : "") +
              `- **Body:** ${body.substring(0, 200)}`,
          }],
          structuredContent: success(
            `Comment added on ${result.chainEntryId} v${result.versionNumber}${result.linkId ? ` [${result.linkId}]` : ""}.`,
            { chainEntryId: result.chainEntryId, versionNumber: result.versionNumber, linkId: result.linkId },
          ),
        };
      }

      if (action === "resolve-comment") {
        if (!commentId) {
          return validationResult("A `commentId` is required.");
        }
        await kernelMutation("gitchain.resolveComment", { commentId });
        return successResult("Comment resolved.", `Comment ${commentId} resolved.`, { commentId, resolved: true });
      }

      if (action === "list-comments") {
        const comments = await kernelMutation<any[]>("gitchain.listComments", {
          chainEntryId, versionNumber,
        });

        if (comments.length === 0) {
          return {
            content: [{ type: "text" as const, text: `No comments found for chain "${chainEntryId}"${versionNumber ? ` v${versionNumber}` : ""}.` }],
            structuredContent: success(
              `No comments for ${chainEntryId}${versionNumber ? ` v${versionNumber}` : ""}.`,
              { chainEntryId, comments: [], total: 0, unresolved: 0 },
            ),
          };
        }

        const formatted = comments
          .map((c: any) => {
            const date = new Date(c.createdAt).toISOString().replace("T", " ").substring(0, 19);
            const resolved = c.resolved ? " (RESOLVED)" : "";
            const link = c.linkId ? ` [${c.linkId}]` : "";
            return `- **v${c.version}${link}** ${date} by ${c.author}${resolved}: ${c.body.substring(0, 150)}`;
          })
          .join("\n");

        const unresolvedCount = comments.filter((c: any) => !c.resolved).length;

        return {
          content: [{
            type: "text" as const,
            text: `# Comments for ${chainEntryId} (${comments.length}, ${unresolvedCount} unresolved)\n\n${formatted}`,
          }],
          structuredContent: success(
            `${comments.length} comment(s) for ${chainEntryId}, ${unresolvedCount} unresolved.`,
            { chainEntryId, total: comments.length, unresolved: unresolvedCount },
          ),
        };
      }

      return unknownAction(action, ["gate", "comment", "resolve-comment", "list-comments"]);
}

// ─── `chain` + `chain-review` compound tools (WP-484 S2, §3 module-gated
// table — SPLIT per coordinator ruling 2026-07-15) ──────────────────────────
// The §3 per-noun ≤12-action ceiling applies with NO module-gated carve-out
// ("a noun that would exceed 12 must split or shed scope"). A single `chain`
// noun absorbing everything would be 17 actions; the ruled fix is a split at
// the namespace seam. The coordinator's first-preference seam (base+version+
// branch in `chain`, review.* separate) still lands at 13 > 12, so the chosen
// seam is the fewest-tools arrangement with every tool ≤12:
//   - `chain`        = base CRUD (4) + version.* (5)              →  9 actions
//   - `chain-review` = legacy review actions, unnamespaced (4)
//                      + branch.* (4)                             →  8 actions
// Both names are REUSED (not "alongside"-eligible); `chain-review` keeps its
// legacy action vocabulary verbatim (gate/comment/resolve-comment/
// list-comments), so pre-S2 `chain-review` calls keep working unchanged.
// WP-484 S3 (clean break, TEN-2157): the legacy `chain-version` and
// `chain-branch` standalone registrations retired below — their handlers
// (handleChainVersion / handleChainBranch) stay live via `chain`
// action=version.* and `chain-review` action=branch.*. Module-gated worst
// case is therefore 13 registered tools (10 core + chain + chain-review +
// map) — still under every derived budget in §1.4.
const CHAIN_ACTIONS = [
  "create", "get", "list", "edit",
  "version.commit", "version.list", "version.diff", "version.revert", "version.history",
] as const;
type ChainAction = (typeof CHAIN_ACTIONS)[number];

const CHAIN_REVIEW_ACTIONS = [
  "gate", "comment", "resolve-comment", "list-comments",
  "branch.create", "branch.list", "branch.merge", "branch.conflicts",
] as const;
type ChainReviewAction = (typeof CHAIN_REVIEW_ACTIONS)[number];

// §3a — the REGISTERED layer for `chain`: flat ZodObject, action enum +
// superset of params (deduped across chainSchema/chainVersionSchema).
export const chainCompoundSchema = z.object({
  action: z.enum(CHAIN_ACTIONS).describe(
    "Unnamespaced: 'create'/'get'/'list'/'edit' — process CRUD (the original `chain` tool). " +
      "'version.*' (commit/list/diff/revert/history) — versioning, absorbs `chain-version`. " +
      "Branching and review live on the sibling `chain-review` tool.",
  ),
  chainEntryId: z.string().max(200).optional().describe("Chain entry ID. Required for get/edit and all version.* actions."),
  title: z.string().max(500).optional().describe("For 'create': process title (required)."),
  chainTypeId: z.string().max(200).optional().default("strategy-coherence").describe("For 'create'/'list': process template slug."),
  description: z.string().max(20000).optional().describe("For 'create': description."),
  linkId: z.string().max(200).optional().describe("For 'edit': link to edit (required)."),
  content: z.string().max(50000).optional().describe("For 'edit': new content for the link (required)."),
  status: z.string().max(200).optional().describe("For 'list': filter by status."),
  author: z.string().max(200).optional().describe("Who is performing the action. Defaults to 'mcp'."),
  commitMessage: z.string().max(2000).optional().describe("For 'version.commit': commit message (required)."),
  versionA: z.number().optional().describe("For 'version.diff': earlier version (required)."),
  versionB: z.number().optional().describe("For 'version.diff': later version (required)."),
  toVersion: z.number().optional().describe("For 'version.revert': version number to revert to (required)."),
});

// §3a — the REGISTERED layer for `chain-review`: legacy review vocabulary
// unnamespaced (pre-S2 call-shape preserved) + branch.* namespaced.
export const chainReviewCompoundSchema = z.object({
  action: z.enum(CHAIN_REVIEW_ACTIONS).describe(
    "'gate'/'comment'/'resolve-comment'/'list-comments' — quality gate + comments (the original `chain-review` tool, unchanged call shape). " +
      "'branch.*' (create/list/merge/conflicts) — branching, absorbs `chain-branch`. " +
      "Process CRUD and versioning live on the sibling `chain` tool.",
  ),
  chainEntryId: z.string().max(200).optional().describe("Chain entry ID. Required for every action except 'resolve-comment'."),
  commitMessage: z.string().max(2000).optional().describe("For 'gate': commit message to lint."),
  versionNumber: z.number().optional().describe("For 'comment'/'list-comments': version to comment on or list comments for."),
  linkId: z.string().max(200).optional().describe("For 'comment': optional link this comment targets."),
  body: z.string().max(20000).optional().describe("For 'comment': comment text (required)."),
  commentId: z.string().max(200).optional().describe("For 'resolve-comment': comment ID (required)."),
  branchName: z.string().max(200).optional().describe("For 'branch.merge'/'branch.conflicts': required. For 'branch.create': optional."),
  strategy: z.enum(["merge_commit", "squash"]).optional().describe("For 'branch.merge': merge strategy. Default 'merge_commit'."),
  author: z.string().max(200).optional().describe("Who is performing the action. Defaults to 'mcp'."),
});

// §3b — the HANDLER layer: per-action discriminatedUnions, reusing the
// existing legacy schemas (Code Integrity) via `.omit({action:true}).extend(...)`.
const chainCreateVariant = chainSchema.omit({ action: true }).extend({ action: z.literal("create") });
const chainGetVariant = z.object({ action: z.literal("get"), chainEntryId: z.string().max(200) });
const chainListVariant = z.object({ action: z.literal("list"), chainTypeId: z.string().max(200).optional(), status: z.string().max(200).optional() });
const chainEditVariant = z.object({
  action: z.literal("edit"),
  chainEntryId: z.string().max(200),
  linkId: z.string().max(200),
  content: z.string().max(50000),
  author: z.string().max(200).optional(),
});
const versionBase = chainVersionSchema.omit({ action: true });
const chainVersionCommitVariant = versionBase.extend({ action: z.literal("version.commit"), commitMessage: z.string().max(2000) });
const chainVersionListVariant = versionBase.extend({ action: z.literal("version.list") });
const chainVersionDiffVariant = versionBase.extend({ action: z.literal("version.diff"), versionA: z.number(), versionB: z.number() });
const chainVersionRevertVariant = versionBase.extend({ action: z.literal("version.revert"), toVersion: z.number() });
const chainVersionHistoryVariant = versionBase.extend({ action: z.literal("version.history") });

export const chainActionUnion = z.discriminatedUnion("action", [
  chainCreateVariant,
  chainGetVariant,
  chainListVariant,
  chainEditVariant,
  chainVersionCommitVariant,
  chainVersionListVariant,
  chainVersionDiffVariant,
  chainVersionRevertVariant,
  chainVersionHistoryVariant,
]);

const reviewBase = chainReviewSchema.omit({ action: true });
// gate/comment/list-comments re-require chainEntryId over reviewBase's optional field
// (finding #12) — only resolve-comment (handleChainReview's commentId-only branch)
// leaves it optional.
const chainReviewGateVariant = reviewBase.extend({ action: z.literal("gate"), chainEntryId: z.string().max(200) });
const chainReviewCommentVariant = reviewBase.extend({ action: z.literal("comment"), chainEntryId: z.string().max(200), versionNumber: z.number(), body: z.string().max(20000) });
const chainReviewResolveCommentVariant = reviewBase.extend({ action: z.literal("resolve-comment"), commentId: z.string().max(200) });
const chainReviewListCommentsVariant = reviewBase.extend({ action: z.literal("list-comments"), chainEntryId: z.string().max(200) });
const branchBase = chainBranchSchema.omit({ action: true });
const chainBranchCreateVariant = branchBase.extend({ action: z.literal("branch.create") });
const chainBranchListVariant = branchBase.extend({ action: z.literal("branch.list") });
const chainBranchMergeVariant = branchBase.extend({ action: z.literal("branch.merge"), branchName: z.string().max(200) });
const chainBranchConflictsVariant = branchBase.extend({ action: z.literal("branch.conflicts"), branchName: z.string().max(200) });

export const chainReviewActionUnion = z.discriminatedUnion("action", [
  chainReviewGateVariant,
  chainReviewCommentVariant,
  chainReviewResolveCommentVariant,
  chainReviewListCommentsVariant,
  chainBranchCreateVariant,
  chainBranchListVariant,
  chainBranchMergeVariant,
  chainBranchConflictsVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const CHAIN_ACTION_SPECS: Record<ChainAction, ActionParamSpec> = {
  create: { params: ["title", "chainTypeId", "description", "author"], description: "title is required." },
  get: { params: ["chainEntryId"], description: "chainEntryId is required." },
  list: { params: ["chainTypeId", "status"], description: "All params optional." },
  edit: { params: ["chainEntryId", "linkId", "content", "author"], description: "chainEntryId, linkId, and content are required." },
  "version.commit": { params: ["chainEntryId", "commitMessage", "author"], description: "chainEntryId and commitMessage are required." },
  "version.list": { params: ["chainEntryId"], description: "chainEntryId is required." },
  "version.diff": { params: ["chainEntryId", "versionA", "versionB"], description: "chainEntryId, versionA, and versionB are required." },
  "version.revert": { params: ["chainEntryId", "toVersion", "author"], description: "chainEntryId and toVersion are required." },
  "version.history": { params: ["chainEntryId"], description: "chainEntryId is required." },
};

/** §4 backpressure text — one entry per action, never used for registration. */
export const CHAIN_REVIEW_ACTION_SPECS: Record<ChainReviewAction, ActionParamSpec> = {
  gate: { params: ["chainEntryId", "commitMessage"], description: "chainEntryId is required." },
  comment: { params: ["chainEntryId", "versionNumber", "linkId", "body", "author"], description: "chainEntryId, versionNumber, and body are required." },
  "resolve-comment": { params: ["commentId"], description: "commentId is required." },
  "list-comments": { params: ["chainEntryId", "versionNumber"], description: "chainEntryId is required." },
  "branch.create": { params: ["chainEntryId", "branchName", "author"], description: "chainEntryId is required." },
  "branch.list": { params: ["chainEntryId"], description: "chainEntryId is required." },
  "branch.merge": { params: ["chainEntryId", "branchName", "strategy", "author"], description: "chainEntryId and branchName are required." },
  "branch.conflicts": { params: ["chainEntryId", "branchName"], description: "chainEntryId and branchName are required." },
};

export function registerGitChainTools(server: McpServer) {
  // §3b handler map for `chain` — strips the namespace prefix and
  // reconstructs the original legacy-schema action value before delegating
  // to the shared handler (handleChainCrud/handleChainVersion).
  const chainHandlers: Record<ChainAction, (data: any) => Promise<ToolResult>> = {
    create: (data) => handleChainCrud({ ...data, action: "create" }),
    get: (data) => handleChainCrud({ ...data, action: "get" }),
    list: (data) => handleChainCrud({ ...data, action: "list" }),
    edit: (data) => handleChainCrud({ ...data, action: "edit" }),
    "version.commit": (data) => handleChainVersion({ ...data, action: "commit" }),
    "version.list": (data) => handleChainVersion({ ...data, action: "list" }),
    "version.diff": (data) => handleChainVersion({ ...data, action: "diff" }),
    "version.revert": (data) => handleChainVersion({ ...data, action: "revert" }),
    "version.history": (data) => handleChainVersion({ ...data, action: "history" }),
  };

  // §3b handler map for `chain-review` — the four unnamespaced actions ARE
  // the legacy vocabulary (pass through verbatim); branch.* strips the prefix
  // before delegating to handleChainBranch.
  const chainReviewHandlers: Record<ChainReviewAction, (data: any) => Promise<ToolResult>> = {
    gate: (data) => handleChainReview({ ...data, action: "gate" }),
    comment: (data) => handleChainReview({ ...data, action: "comment" }),
    "resolve-comment": (data) => handleChainReview({ ...data, action: "resolve-comment" }),
    "list-comments": (data) => handleChainReview({ ...data, action: "list-comments" }),
    "branch.create": (data) => handleChainBranch({ ...data, action: "create" }),
    "branch.list": (data) => handleChainBranch({ ...data, action: "list" }),
    "branch.merge": (data) => handleChainBranch({ ...data, action: "merge" }),
    "branch.conflicts": (data) => handleChainBranch({ ...data, action: "conflicts" }),
  };

  const chainTool = server.registerTool(
    "chain",
    {
      title: "Chain",
      description:
        "Manage processes — CRUD and versioning. Processes are versioned knowledge " +
        "artifacts that follow a process template (e.g. Strategy Coherence: Problem → Insight → Choice → " +
        "Action → Outcome). Module-gated (PB_MODULES=gitchain).\n\n" +
        "- **create/get/list/edit**: process CRUD.\n" +
        "- **version.commit/version.list/version.diff/version.revert/version.history**: versioning (absorbs `chain-version`).\n\n" +
        "Branching and review live on the sibling `chain-review` tool.",
      inputSchema: chainCompoundSchema,
      annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(chainCompoundSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "chain", action }, () =>
        dispatchDiscriminated("chain", chainActionUnion, parsed.data, CHAIN_ACTION_SPECS, chainHandlers),
      );
    }),
  );
  trackWriteTool(chainTool);

  const chainReviewTool = server.registerTool(
    "chain-review",
    {
      title: "Chain Review",
      description:
        "Review process quality and manage branches. Module-gated (PB_MODULES=gitchain).\n\n" +
        "- **gate**: run the coherence gate — checks coherence score >= 70%, all links filled, commit message convention.\n" +
        "- **comment/resolve-comment/list-comments**: manage comments on process versions.\n" +
        "- **branch.create/branch.list/branch.merge/branch.conflicts**: branching (absorbs `chain-branch`).\n\n" +
        "Process CRUD and versioning live on the sibling `chain` tool.",
      inputSchema: chainReviewCompoundSchema,
      annotations: { readOnlyHint: false, destructiveHint: false, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(chainReviewCompoundSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "chain-review", action }, () =>
        dispatchDiscriminated("chain-review", chainReviewActionUnion, parsed.data, CHAIN_REVIEW_ACTION_SPECS, chainReviewHandlers),
      );
    }),
  );
  trackWriteTool(chainReviewTool);

  // WP-484 S3 (clean break, TEN-2157): the standalone `chain-version` and
  // `chain-branch` registrations retired here. handleChainVersion /
  // handleChainBranch stay live via `chain` action=version.* and
  // `chain-review` action=branch.* (chainHandlers / chainReviewHandlers,
  // above).
}
